import { compareStrings } from '@protector/utils/string';
import type { Feature, FeatureCollection, Point, Polygon } from 'geojson';
import type { TFunction } from 'i18next';
import type {
  RegionalOverviewDigitalTrapsProperties,
  RegionalOverviewPropertiesUnion
} from 'querys/regional-overview/regional-overview.query.models';
import { isRegionalOverviewDigitalTrapsProperties } from 'querys/regional-overview/regional-overview.query.utils';
import type { NetworkSelectOption, RiskLevel, TrapGroup, TrapMarker } from '../domain';
import { translateEppoCode, translateGroupName } from './group.utils';
import { calculateRiskLevelFromThreshold, mapDailyCountsKeys } from './trap-pest-pressure.utils';

const RISK_LEVEL_TO_STATUS_MAP: Record<RiskLevel, 'success' | 'warning' | 'error' | 'default'> = {
  low: 'success',
  medium: 'warning',
  high: 'error',
  not_applicable: 'default',
  no_threshold: 'default'
};

export const mapFeatureToTrapMarker = (
  trapData: Feature<Point | Polygon, RegionalOverviewPropertiesUnion>,
  dateKeys: string[]
): TrapMarker | undefined => {
  const coordinates = trapData.geometry.coordinates;

  if (!isRegionalOverviewDigitalTrapsProperties(trapData.properties)) {
    return;
  }

  const { trapCount, hasCountData } = mapDailyCountsKeys(trapData.properties.details.daily_insect_counts, dateKeys);
  const popupCount = hasCountData ? trapCount : '--';
  const popupRiskLevel = calculateRiskLevelFromThreshold(popupCount, trapData.properties.threshold);

  return {
    id: trapData.properties.id,
    name: trapData.properties.name,
    longitude: Number(coordinates[0]),
    latitude: Number(coordinates[1]),
    riskLevel: trapData.properties.level,
    count: trapData.properties.total_number_pests,
    popupRiskLevel,
    popupCount,
    pestName: trapData.properties.group.pest_eppo_name,
    cropName: trapData.properties.group.crop_eppo_name
  };
};

export const createTrapGroups = (features: Feature<Point | Polygon, RegionalOverviewPropertiesUnion>[]): TrapGroup[] => {
  const groupMap = new Map<string, TrapGroup>();

  features.forEach(trap => {
    if (!isRegionalOverviewDigitalTrapsProperties(trap.properties)) {
      return;
    }

    const props = trap.properties;
    const groupId = props.group.id;

    if (!groupMap.has(groupId)) {
      groupMap.set(groupId, {
        id: props.group.id,
        title: props.group.name,
        count: 0,
        crop: props.group.crop_eppo_name || '--',
        lastUpdate: new Date(props.last_update).toLocaleDateString(),
        pestEppoCode: props.group.pest_eppo_code,
        pestLabel: props.group.pest_eppo_name || '--',
        customGroup: props.group.custom_group,
        trapList: [],
        trapsInGroup: []
      });
    }

    const group = groupMap.get(groupId);
    if (group) {
      group.trapList.push({
        id: props.id,
        name: props.name,
        count: props.total_number_pests,
        status: RISK_LEVEL_TO_STATUS_MAP[props.level],
        flightPredictions: props.flight_predictions
      });

      group.trapsInGroup.push(trap as Feature<Point, RegionalOverviewDigitalTrapsProperties>);
      group.count = group.trapList.length;

      group?.trapList.sort((a, b) => {
        const aCount = typeof a.count === 'string' ? -1 : a.count;
        const bCount = typeof b.count === 'string' ? -1 : b.count;
        return bCount - aCount;
      });
    }
  });

  return Array.from(groupMap.values()).sort((a, b) => compareStrings(a.title ?? '', b.title ?? '') || compareStrings(a.id, b.id));
};

export const calculateTotalTraps = (trapGroups: TrapGroup[]): number => {
  return trapGroups.reduce((acc, group) => acc + group.trapList.length, 0);
};

export const translateGroupInfo = (
  allFeatures: FeatureCollection<Point, RegionalOverviewDigitalTrapsProperties>,
  t: TFunction
): FeatureCollection<Point, RegionalOverviewDigitalTrapsProperties> => {
  if (!allFeatures?.features) {
    return {
      type: 'FeatureCollection',
      features: []
    };
  }

  return {
    ...allFeatures,
    features: allFeatures.features.map(feature => {
      if (!isRegionalOverviewDigitalTrapsProperties(feature.properties)) {
        return feature;
      }

      const { group, ...rest } = feature.properties;
      const { pest_eppo_code, crop_eppo_code, pest_eppo_name, crop_eppo_name } = group;

      return {
        ...feature,
        properties: {
          ...rest,
          group: {
            ...group,
            name: translateGroupName(group, t),
            pest_eppo_name: pest_eppo_code ? translateEppoCode('pest_eppo_code', pest_eppo_code, t) : pest_eppo_name,
            crop_eppo_name: crop_eppo_code ? translateEppoCode('crop_eppo_code', crop_eppo_code, t) : crop_eppo_name
          }
        }
      };
    })
  };
};

export const mapTrapItemToOption = (trapItem: TrapGroup): NetworkSelectOption => ({
  ...trapItem,
  label: trapItem.title,
  value: trapItem.id
});
